import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Upload, Trash2, ShieldCheck, FolderOpen, Sparkles, Feather, Inbox, QrCode } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { uploadFile } from "../lib/uploadFile";
import { useAuth } from "../context/AuthContext";
import { Spinner, SectionLabel, Button, Pill } from "../components/Primitives";
import AdminAssignments from "../components/AdminAssignments";
import AdminStories from "../components/AdminStories";
import AdminSubmissions from "../components/AdminSubmissions";
import AdminScans from "../components/AdminScans";

export default function AdminFiles() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("library");
  const [files, setFiles] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [kingdoms, setKingdoms] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category: "", attach_type: "resource", attach_slug: "" });
  const [selected, setSelected] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadFiles = () => api.get("/files").then((r) => setFiles(r.data)).catch(() => {});

  useEffect(() => {
    if (!user?.is_admin) return;
    loadFiles();
    api.get("/lessons").then((r) => setLessons(r.data)).catch(() => {});
    api.get("/kingdoms").then((r) => setKingdoms(r.data)).catch(() => {});
  }, [user]);

  if (loading) return <div className="min-h-[60vh]"><Spinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="mx-auto max-w-xl px-6 py-32 text-center" data-testid="admin-denied">
        <ShieldCheck className="mx-auto h-10 w-10 text-gold" />
        <h1 className="mt-4 font-serif text-3xl text-bone">Administrators only</h1>
        <p className="mt-2 text-muted">This area is restricted to curriculum administrators.</p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a file to upload");
    if ((form.attach_type === "lesson" || form.attach_type === "kingdom") && !form.attach_slug) {
      return toast.error(`Select a ${form.attach_type} to attach to`);
    }
    setUploading(true);
    setProgress(0);
    try {
      await uploadFile(file, {
        title: form.title || file.name,
        description: form.description,
        category: form.category,
        attach_type: form.attach_type,
        attach_slug: form.attach_slug,
      }, setProgress);
      toast.success("File uploaded to the library");
      setForm({ title: "", description: "", category: "", attach_type: "resource", attach_slug: "" });
      if (fileRef.current) fileRef.current.value = "";
      setSelected(null);
      loadFiles();
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/files/${id}`);
    toast("File removed");
    loadFiles();
  };

  const input = "w-full border border-edge bg-ink px-4 py-3 text-bone placeholder:text-muted focus:border-gold focus:outline-none";

  return (
    <div data-testid="admin-page" className="mx-auto max-w-7xl px-6 py-12 md:px-12">
      <SectionLabel>Administration</SectionLabel>
      <h1 className="font-serif text-4xl font-semibold text-bone lg:text-5xl">Curriculum Materials</h1>

      {/* TABS */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-tabs">
        <button onClick={() => setTab("library")} data-testid="tab-library"
          className={`flex items-center gap-2 border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${tab === "library" ? "border-gold bg-gold text-ink" : "border-edge text-bone hover:border-gold/60"}`}>
          <FolderOpen className="h-4 w-4" /> File Library
        </button>
        <button onClick={() => setTab("assignments")} data-testid="tab-assignments"
          className={`flex items-center gap-2 border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${tab === "assignments" ? "border-gold bg-gold text-ink" : "border-edge text-bone hover:border-gold/60"}`}>
          <Sparkles className="h-4 w-4" /> Generate Assignments
        </button>
        <button onClick={() => setTab("stories")} data-testid="tab-stories"
          className={`flex items-center gap-2 border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${tab === "stories" ? "border-gold bg-gold text-ink" : "border-edge text-bone hover:border-gold/60"}`}>
          <Feather className="h-4 w-4" /> Moderate Stories
        </button>
        <button onClick={() => setTab("submissions")} data-testid="tab-submissions"
          className={`flex items-center gap-2 border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${tab === "submissions" ? "border-gold bg-gold text-ink" : "border-edge text-bone hover:border-gold/60"}`}>
          <Inbox className="h-4 w-4" /> Review Submissions
        </button>
        <button onClick={() => setTab("scans")} data-testid="tab-scans"
          className={`flex items-center gap-2 border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${tab === "scans" ? "border-gold bg-gold text-ink" : "border-edge text-bone hover:border-gold/60"}`}>
          <QrCode className="h-4 w-4" /> QR Scans
        </button>
      </div>

      {tab === "assignments" ? (
        <>
          <p className="mt-6 max-w-2xl text-muted">Drop a PDF or text file, preview its content, and let the AI create short-answer questions and a writing task — then publish them for students.</p>
          <AdminAssignments />
        </>
      ) : tab === "stories" ? (
        <>
          <p className="mt-6 max-w-2xl text-muted">Review submitted heritage stories.</p>
          <AdminStories />
        </>
      ) : tab === "submissions" ? (
        <>
          <p className="mt-6 max-w-2xl text-muted">Read student responses to published assignments and leave written feedback — students see it on their assignment page.</p>
          <AdminSubmissions />
        </>
      ) : tab === "scans" ? (
        <>
          <p className="mt-6 max-w-2xl text-muted">How many students scanned the QR code on printed worksheets to answer online.</p>
          <AdminScans />
        </>
      ) : (
      <>
      <p className="mt-6 max-w-2xl text-muted">Upload documents, images, video, and audio. Uploads are viewable by everyone in the browser.</p>

      <div className="mt-10 grid gap-12 lg:grid-cols-12">
        {/* UPLOAD FORM */}
        <form onSubmit={submit} className="border border-edge bg-ash p-6 lg:col-span-5" data-testid="upload-form">
          <h2 className="mb-5 font-serif text-2xl text-bone">Add a file</h2>

          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-gold">Title</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Map of Trans-Saharan Trade Routes" data-testid="file-title-input" className={`${input} mb-4`} />

          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-gold">Description</label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Optional context for educators" data-testid="file-desc-input" className={`${input} mb-4`} />

          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-gold">Category</label>
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Primary Source, Handout, Map" data-testid="file-category-input" className={`${input} mb-4`} />

          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-gold">Attach to</label>
          <select value={form.attach_type} onChange={(e) => setForm({ ...form, attach_type: e.target.value, attach_slug: "" })} data-testid="attach-type-select" className={`${input} mb-4`}>
            <option value="resource">Library (standalone)</option>
            <option value="lesson">A Lesson</option>
            <option value="kingdom">A Kingdom</option>
          </select>

          {form.attach_type === "lesson" && (
            <select value={form.attach_slug} onChange={(e) => setForm({ ...form, attach_slug: e.target.value })} data-testid="attach-slug-lesson" className={`${input} mb-4`}>
              <option value="">Select a lesson…</option>
              {lessons.map((l) => <option key={l.slug} value={l.slug}>{l.title}</option>)}
            </select>
          )}
          {form.attach_type === "kingdom" && (
            <select value={form.attach_slug} onChange={(e) => setForm({ ...form, attach_slug: e.target.value })} data-testid="attach-slug-kingdom" className={`${input} mb-4`}>
              <option value="">Select a kingdom…</option>
              {kingdoms.map((k) => <option key={k.slug} value={k.slug}>{k.name}</option>)}
            </select>
          )}

          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-gold">File</label>
          <input ref={fileRef} type="file" onChange={(e) => setSelected(e.target.files?.[0]?.name || null)} data-testid="file-input" className={`${input} mb-4 file:mr-4 file:border-0 file:bg-gold file:px-3 file:py-1 file:text-ink`} />
          {selected && <p className="mb-4 text-xs text-muted">Selected: {selected}</p>}

          {uploading && (
            <div className="mb-4">
              <div className="h-1.5 w-full bg-edge">
                <div className="h-1.5 bg-gold transition-all" style={{ width: `${progress}%` }} data-testid="upload-progress" />
              </div>
              <p className="mt-1 font-mono text-[11px] text-muted">{progress}% uploaded</p>
            </div>
          )}

          <Button type="submit" variant="gold" className="w-full" disabled={uploading} data-testid="upload-btn">
            <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload File"}
          </Button>
        </form>

        {/* FILE LIST */}
        <div className="lg:col-span-7">
          <h2 className="mb-5 font-serif text-2xl text-bone">Library ({files.length})</h2>
          {files.length === 0 ? (
            <p className="border border-dashed border-edge p-12 text-center text-muted" data-testid="admin-empty">No files uploaded yet.</p>
          ) : (
            <div className="space-y-3">
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-4 border border-edge bg-ash p-4" data-testid={`admin-file-${f.id}`}>
                  <div className="flex-1">
                    <p className="text-bone">{f.title}</p>
                    <p className="text-xs text-muted">{f.original_filename} · {f.attach_type}{f.attach_slug ? ` · ${f.attach_slug}` : ""}</p>
                  </div>
                  <Pill tone="muted">{f.kind}</Pill>
                  <button onClick={() => remove(f.id)} data-testid={`delete-file-${f.id}`} className="text-muted hover:text-blood">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
